import { create } from 'zustand'
import type { GridApi } from 'ag-grid-community'
import type { TableRow } from '../../modules/table/types'
import { queryClient } from '../api/queryClient'
import { cancelQueueRefresh, scheduleQueueRefresh } from './queueRefresh'

// ── Батчинг WS-инвалидаций ────────────────────────────────────────────────
//
// На горячих сценариях (массовый импорт квот, bulk-обновление позиций) бэк
// шлёт десятки broadcast'ов за секунду. Каждый раньше вызывал N синхронных
// queryClient.invalidateQueries — это давало шторм GET'ов на сервер (в логах
// 22 одинаковых GET /requests/{id} за 30с — всё это рефетчи на WS-события).
//
// Теперь собираем ключи в окне 150мс: если за это время прилетело 50 событий
// с одним и тем же ключом ['requests'], это одна инвалидация на выходе.
// Window 150мс — достаточно мало для ощущения realtime, достаточно много
// чтобы схлопнуть взрывной поток событий.
const pendingInvalidations = new Map<string, unknown[]>()
let invalidationFlushTimer: ReturnType<typeof setTimeout> | null = null

function scheduleInvalidate(queryKey: unknown[]): void {
  const serialized = JSON.stringify(queryKey)
  pendingInvalidations.set(serialized, queryKey)
  if (invalidationFlushTimer) return
  invalidationFlushTimer = setTimeout(() => {
    invalidationFlushTimer = null
    const keys = Array.from(pendingInvalidations.values())
    pendingInvalidations.clear()
    for (const key of keys) {
      void queryClient.invalidateQueries({ queryKey: key })
    }
  }, 150)
}

// Reverse mapping: API field name → TableRow field name
const API_TO_TABLE: Partial<Record<string, keyof TableRow>> = {
  partNumber: 'partNumber',
  brand: 'brand',
  quantity: 'qty',
  unit: 'unit',
  priority: 'priority',
  targetPrice: 'targetPrice',
  noteForProcurement: 'comments',
  status: 'status',
}

type WsStatus = 'disconnected' | 'connecting' | 'connected'

export interface RealtimeState {
  status: WsStatus
  gridApi: GridApi<TableRow> | null
  setGridApi: (api: GridApi<TableRow> | null) => void
  connect: () => void
  disconnect: () => void
}

// Module-level refs — not Zustand state (not serializable)
let socket: WebSocket | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let active = false
let reconnectAttempts = 0
// Сколько раз подряд WS закрылся, так и не открывшись (onclose без onopen).
// Растёт при rate-limit (429), сетевых отказах, истёкшей cookie. Если
// бьём в потолок — прекращаем реконнект до следующего ручного connect()
// (логин / обновление страницы), иначе фронт молотит сервер впустую.
let consecutiveFailsWithoutOpen = 0

// Экспоненциальный backoff: 1с, 2с, 4с, 8с, 16с, 30с (потолок)
// Защищает API от шторма реконнектов при массовом падении клиентов.
const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30_000
// Потолок безуспешных подряд попыток без onopen. После него — стоп.
// Покрывает ~31с экспоненциального backoff'а — достаточно, чтобы
// пережить штатный перезапуск api при деплое, и одновременно не дать
// зависшей вкладке с протухшей cookie долбить /api/v1/ws сутками.
const MAX_FAILS_WITHOUT_OPEN = 5
// Кастомный close-code от сервера: «не авторизован» (apps/api/src/modules/realtime/ws.routes.ts).
// При нём реконнект бесполезен — нужен новый логин.
const WS_CLOSE_UNAUTHORIZED = 4401

function nextReconnectDelay(): number {
  return Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** reconnectAttempts)
}

function buildWsUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${proto}://${window.location.host}/api/v1/ws`
}

function applyItemUpdate(
  gridApi: GridApi<TableRow> | null,
  payload: { id: string; fields: Record<string, unknown> },
) {
  const tableUpdates: Partial<TableRow> = {}
  for (const [apiField, value] of Object.entries(payload.fields)) {
    const tableField = API_TO_TABLE[apiField]
    if (tableField !== undefined) {
      const mutable = tableUpdates as Record<string, unknown>
      mutable[tableField] = value
    }
  }
  if (Object.keys(tableUpdates).length === 0) return

  // Point-update the AG Grid row (no full re-render of other rows).
  // Страница «Таблица» могла уйти с экрана (grid destroyed), но в store
  // ссылка на api ещё осталась — вызов любого метода даёт AG Grid
  // warning #26. Проверяем isDestroyed().
  if (gridApi && !gridApi.isDestroyed()) {
    const node = gridApi.getRowNode(payload.id)
    if (node?.data) {
      node.setData({ ...node.data, ...tableUpdates })
    }
  }

  // Sync TanStack Query cache so subsequent refetches don't undo the update
  queryClient.setQueryData(
    ['table-items'],
    (old: { rows: TableRow[]; total: number; page: number } | undefined) => {
      if (!old) return old
      return {
        ...old,
        rows: old.rows.map((row) => (row.id === payload.id ? { ...row, ...tableUpdates } : row)),
      }
    },
  )
}

export const useRealtimeStore = create<RealtimeState>((set, get) => ({
  status: 'disconnected',
  gridApi: null,

  setGridApi: (api) => set({ gridApi: api }),

  connect: () => {
    if (socket && socket.readyState === WebSocket.OPEN) return
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    active = true
    // Ручной вход в connect() — это всегда «свежая» попытка: сбрасываем
    // счётчик подряд неудач, чтобы после логина / F5 реконнект работал
    // даже если до этого мы упёрлись в MAX_FAILS_WITHOUT_OPEN.
    consecutiveFailsWithoutOpen = 0
    set({ status: 'connecting' })

    const ws = new WebSocket(buildWsUrl())
    socket = ws
    let wasOpen = false

    ws.onopen = () => {
      wasOpen = true
      consecutiveFailsWithoutOpen = 0
      // Если это реконнект после реального обрыва (а не первый коннект),
      // дёргаем ключевые queries: за время обрыва мог уйти broadcast, и
      // данные на экране устарели. Триггер только по WS-реконнекту —
      // не по каждому фокусу вкладки, чтобы не создавать шторм запросов
      // при 100+ юзерах с несколькими вкладками.
      const wasReconnect = reconnectAttempts > 0
      reconnectAttempts = 0
      set({ status: 'connected' })
      if (wasReconnect) {
        scheduleInvalidate(['requests'])
        scheduleInvalidate(['dashboard'])
        scheduleQueueRefresh()
        scheduleInvalidate(['orders'])
      }
    }

    ws.onmessage = (event: MessageEvent<string>) => {
      let msg: { event: string; payload: unknown }
      try {
        msg = JSON.parse(event.data) as { event: string; payload: unknown }
      } catch {
        return
      }

      if (msg.event === 'request_item.updated') {
        const payload = msg.payload as {
          id: string
          requestId?: string
          fields: Record<string, unknown>
        }
        applyItemUpdate(get().gridApi, payload)

        // Точечно обновляем деталь заявки — без рефетча с сервера.
        // Закрывает P1-3: на горячем пути правки полей позиции список
        // заявок, procurement-results и dashboard НЕ инвалидируем —
        // они не зависят от одного поля позиции и при необходимости
        // обновятся при переходе/фокусе.
        if (payload.requestId) {
          queryClient.setQueryData<{ items?: { id: string }[] } | undefined>(
            ['requests', payload.requestId],
            (old) => {
              if (!old?.items) return old
              return {
                ...old,
                items: old.items.map((it) =>
                  it.id === payload.id ? { ...it, ...payload.fields } : it,
                ),
              }
            },
          )
          // ActionsPanel показывает счётчики Готовы/В работе/Нужна инфо —
          // они зависят от статуса позиции и должны обновиться без F5.
          if ('status' in payload.fields) {
            scheduleInvalidate(['readiness', payload.requestId])
          }
        }
      }

      if (msg.event === 'request_item.deleted') {
        const payload = msg.payload as { id: string; requestId: string }
        scheduleInvalidate(['requests', payload.requestId])
        scheduleInvalidate(['requests'])
        scheduleInvalidate(['table-items'])
        scheduleInvalidate(['dashboard'])
        // Удалённая позиция пропадает из очереди квот (РОЗ, ГД) без F5.
        scheduleQueueRefresh()
      }

      if (msg.event === 'request.deleted') {
        const payload = msg.payload as { id: string }
        scheduleInvalidate(['requests'])
        scheduleInvalidate(['requests', payload.id])
        scheduleInvalidate(['dashboard'])
      }

      if (msg.event === 'procurement_result.updated') {
        const payload = msg.payload as { requestItemId: string }
        scheduleInvalidate(['procurement-results', payload.requestItemId])
        scheduleInvalidate(['procurement-results'])
        scheduleInvalidate(['requests'])
        scheduleInvalidate(['dashboard'])
        scheduleInvalidate(['table-items'])
        scheduleQueueRefresh()
        // Выпадашка «Позиции заявки» в RequestItemPage показывает ✓
        // у позиций с квотой текущего закупщика. Без этой инвалидации
        // галочка появлялась только после F5.
        scheduleInvalidate(['request-items-by-request'])
        // Показатели клиента (Сумма по КП, WON/QUOTE_SENT) зависят от
        // quotation_snapshots, но сумма пересчитывается сервером при
        // создании квоты/КП — страхуемся на случай отображения.
        scheduleInvalidate(['clients'])
        // Per-user закупщицкий state автоматически становится QUOTED при
        // создании квоты — обновляем бейдж позиции без F5.
        scheduleInvalidate(['item-proc-state', payload.requestItemId])
      }

      if (msg.event === 'procurement_state.updated') {
        const payload = msg.payload as { requestItemId: string; requestId: string }
        scheduleInvalidate(['item-proc-state', payload.requestItemId])
        scheduleInvalidate(['requests', payload.requestId])
        scheduleInvalidate(['requests'])
        scheduleQueueRefresh()
        scheduleInvalidate(['dashboard'])
      }

      if (msg.event === 'comment.created') {
        const payload = msg.payload as { requestId?: string; requestItemId?: string }
        if (payload.requestId) {
          scheduleInvalidate(['comments', 'request', payload.requestId])
        }
        if (payload.requestItemId) {
          scheduleInvalidate(['comments', 'item', payload.requestItemId])
        }
        scheduleInvalidate(['requests'])
        // Для дашборда менеджера — обновить ленту событий и счётчик «Комментарии».
        scheduleInvalidate(['dashboard'])
      }

      if (msg.event === 'comment.updated' || msg.event === 'comment.deleted') {
        const payload = msg.payload as {
          requestId?: string | null
          requestItemId?: string | null
        }
        if (payload.requestId) {
          scheduleInvalidate(['comments', 'request', payload.requestId])
        }
        if (payload.requestItemId) {
          scheduleInvalidate(['comments', 'item', payload.requestItemId])
        }
      }

      if (msg.event === 'request.updated') {
        const payload = msg.payload as {
          id?: string
          status?: string
          isPartiallyProcessed?: boolean
        }
        // Точечное обновление detail-query заявки без рефетча: автопереход
        // статуса (IN_PROGRESS → QUOTATION_READY → QUOTATION_CREATED → ...)
        // моментально отражается на открытой странице без F5.
        if (payload.id && payload.status) {
          queryClient.setQueryData<Record<string, unknown> | undefined>(
            ['requests', payload.id],
            (old) => {
              if (!old) return old
              return {
                ...old,
                status: payload.status,
                ...(typeof payload.isPartiallyProcessed === 'boolean'
                  ? { isPartiallyProcessed: payload.isPartiallyProcessed }
                  : {}),
              }
            },
          )
        } else if (payload.id) {
          scheduleInvalidate(['requests', payload.id])
        }
        // Readiness для блока «Действия» зависит от статусов позиций.
        if (payload.id) {
          scheduleInvalidate(['readiness', payload.id])
        }
        scheduleInvalidate(['requests'])
        scheduleInvalidate(['dashboard'])
        // Карточка клиента показывает «В работе/КП отправлено/Оплачено» —
        // счётчики зависят от статуса заявки. Без этого карточка отставала.
        scheduleInvalidate(['clients'])
      }

      // Массовый импорт позиций — у всех, кто открыл заявку, список обновится без F5
      if (msg.event === 'request.items_bulk_created') {
        const payload = msg.payload as { requestId: string; count: number }
        scheduleInvalidate(['requests', payload.requestId])
        scheduleInvalidate(['request-items', payload.requestId])
        scheduleInvalidate(['requests'])
        scheduleInvalidate(['dashboard'])
      }

      // Массовая смена матрицы у позиций заявки (чекбоксы + «Применить»).
      // Один broadcast на всю пачку — инвалидируем заявку и списки.
      if (msg.event === 'request_item.bulk_updated') {
        const payload = msg.payload as { requestId: string; itemIds: string[]; fields?: string[] }
        scheduleInvalidate(['requests', payload.requestId])
        scheduleInvalidate(['request-items', payload.requestId])
        // Грид позиций в карточке заявки кэшируется под ['request-items-by-request']
        // — без этого ключа «Назначен»/матрица не обновлялись бы realtime.
        scheduleInvalidate(['request-items-by-request'])
        scheduleInvalidate(['requests'])
        scheduleInvalidate(['dashboard'])
        // Назначение закупщика меняет видимость позиций в очереди квот: массовое
        // назначение и снятие назначений при блокировке закупщика (№422).
        if (payload.fields?.includes('assignedToId')) {
          scheduleQueueRefresh()
        }
      }

      // Массовый импорт ответа поставщика — один broadcast вместо N квот.
      // Ключи — как у procurement_result.updated: очередь квот, «Таблица»,
      // грид позиций карточки, список заявок и рабочие статусы закупщика.
      if (msg.event === 'request.procurement_results_bulk_created') {
        scheduleInvalidate(['requests'])
        scheduleInvalidate(['procurement-results'])
        scheduleInvalidate(['dashboard'])
        scheduleInvalidate(['table-items'])
        scheduleQueueRefresh()
        scheduleInvalidate(['request-items-by-request'])
        scheduleInvalidate(['item-proc-state'])
      }

      if (msg.event === 'notification.created') {
        scheduleInvalidate(['notifications'])
      }

      if (msg.event === 'notifications.unread_count.updated') {
        // Префиксно — вся ветка: и счётчик колокольчика, и инфинит-лента
        // страницы (кросс-таб «Прочитать все» перестраивает isRead-first
        // порядок — offset-страницы обязаны перефетчиться, ревью 03.08).
        scheduleInvalidate(['notifications'])
      }

      if (msg.event === 'notification.read') {
        scheduleInvalidate(['notifications'])
      }

      if (
        msg.event === 'news.created' ||
        msg.event === 'news.updated' ||
        msg.event === 'news.deleted'
      ) {
        scheduleInvalidate(['news'])
      }

      if (msg.event === 'request.procurement_completion.updated') {
        const payload = msg.payload as { requestId: string }
        scheduleInvalidate(['request-procurement-completion', payload.requestId])
        scheduleInvalidate(['requests'])
      }

      // Блок 2 «Заказы»: создание/обновление/удаление заказа — обновляем
      // список заказов, карточку и дашборд без F5.
      if (
        msg.event === 'order.created' ||
        msg.event === 'order.updated' ||
        msg.event === 'order.deleted'
      ) {
        const payload = msg.payload as { orderId?: string }
        if (payload.orderId) scheduleInvalidate(['orders', payload.orderId])
        scheduleInvalidate(['orders'])
        scheduleInvalidate(['dashboard'])
      }

      // Смена владельца клиента: у прошлого/нового владельца меняются списки
      // клиентов и заявок (видимость), а также дашборды/планирование.
      if (msg.event === 'client.owner_reassigned') {
        const payload = msg.payload as { clientId?: string }
        if (payload.clientId) scheduleInvalidate(['clients', payload.clientId])
        scheduleInvalidate(['clients'])
        scheduleInvalidate(['requests'])
        scheduleInvalidate(['dashboard'])
      }
    }

    ws.onclose = (event: CloseEvent) => {
      socket = null
      set({ status: 'disconnected' })
      if (!active) return

      // Если open не случился — попытка не удалась. Кейсы: rate-limit 429
      // на /api/v1/ws (общий офисный IP с десятками вкладок), сетевая
      // ошибка, отказ сервера. Если open был — это нормальное падение
      // живого соединения, реконнект через backoff.
      if (!wasOpen) {
        consecutiveFailsWithoutOpen += 1
      } else {
        consecutiveFailsWithoutOpen = 0
      }

      // Сервер закрыл нас как неавторизованного (cookie протухла) —
      // реконнект бесполезен, пока пользователь не залогинится заново.
      // Останавливаемся; следующая попытка — при login() или fetchMe().
      if (event.code === WS_CLOSE_UNAUTHORIZED) {
        active = false
        return
      }

      // Защитный потолок: если N подряд попыток подряд провалились
      // (типичный сценарий — оставленная вкладка с истёкшей сессией +
      // rate-limit на IP) — прекращаем реконнект, чтобы не засорять
      // мониторинг и не давить на api. Восстановится при F5 или логине.
      if (consecutiveFailsWithoutOpen >= MAX_FAILS_WITHOUT_OPEN) {
        active = false
        return
      }

      const delay = nextReconnectDelay()
      reconnectAttempts += 1
      reconnectTimer = setTimeout(() => {
        get().connect()
      }, delay)
    }

    ws.onerror = () => {
      ws.close()
    }
  },

  disconnect: () => {
    cancelQueueRefresh()
    active = false
    reconnectAttempts = 0
    consecutiveFailsWithoutOpen = 0
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    // Сбросить очередь отложенных инвалидаций — иначе после logout
    // через 150мс выстрелят GET'ы на старых ключах с новой (анонимной)
    // сессией и поймают 401, либо после hot-reload в dev дёрнут
    // устаревший queryClient.
    if (invalidationFlushTimer) {
      clearTimeout(invalidationFlushTimer)
      invalidationFlushTimer = null
    }
    pendingInvalidations.clear()
    if (socket) {
      socket.close()
      socket = null
    }
    set({ status: 'disconnected' })
  },
}))
